import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Input from '../../../components/input/Input';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import useManageTravelCard from '../../setting/travelCard/useManageTravelCard';
import { numberFields, validateNumberInput } from '../../../utils/formValidations';
import { toast } from 'react-toastify';
import DatePicker from "react-datepicker";
import { formatDate } from '../../../utils/dateUtils';
import { formatAmount } from '../../../utils/formatAmount';
import useWithdrawRecord from './useWithdrawRecord';
import WithdrawRecordService from './WithdrawRecordService';

const formatLocalYMD = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getInitialFormData = () => ({
  cost: '',
  qty: '',
  cardName: '',
  withdrawDate: new Date(),
  cashWithdrawFee: '',
  cashWithdrawFeeJpy: '',
  totalFee: '',
});

/** Normalize any API date to YYYY-MM-DD for grouping */
const toDateKey = (d) => {
  if (d == null || d === '') return '';
  if (typeof d === 'string' && d.length >= 10) return d.slice(0, 10);
  try {
    const dt = new Date(d);
    if (!Number.isNaN(dt.getTime())) {
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
  } catch (_) { /* ignore */ }
  return String(d).slice(0, 10);
};

/** Ag-grid cell: per-card chips (times × qty units, fee per card) */
const CardBreakdownCell = (params) => {
  const byCard = params.data?.byCard || {};
  const entries = Object.entries(byCard).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex flex-wrap gap-2 py-1.5">
      {entries.map(([name, { qty, fee }]) => (
        <span
          key={name}
          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs shadow-sm"
        >
          <span className="font-semibold text-slate-800">{name}</span>
          <span className="text-slate-400">·</span>
          <span className="text-slate-600">×{qty}</span>
          <span className="text-slate-400">·</span>
          <span className="font-medium text-emerald-800">฿{formatAmount(fee)}</span>
        </span>
      ))}
    </div>
  );
};

const WithdrawRecord = () => {

  const [formData, setFormData] = useState(() => getInitialFormData());
  const [editingId, setEditingId] = useState(null);
  const [bulkItems, setBulkItems] = useState([]);
  const [bulkCardName, setBulkCardName] = useState('');
  const [bulkQty, setBulkQty] = useState('');
  const {travelCards, refreshTravelCards} = useManageTravelCard()
  const {withdrawRecords, saveWithdrawRecord, saveWithdrawRecordsBulk, getWithdrawRecords} = useWithdrawRecord()
  const [expandedDateKey, setExpandedDateKey] = useState(null);
  const [rateInfo, setRateInfo] = useState(null);
  const [rateError, setRateError] = useState(null);

  const daySummaryRows = useMemo(() => {
    const map = new Map();
    for (const r of withdrawRecords) {
      const key = toDateKey(r.withdrawDate);
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    const rows = [];
    for (const [dateKey, records] of map) {
      const byCard = {};
      let dayTotal = 0;
      for (const r of records) {
        const name = r.cardName || '—';
        if (!byCard[name]) byCard[name] = { qty: 0, fee: 0 };
        const q = Number(r.qty) || 0;
        const f = Number(r.totalFee) || 0;
        byCard[name].qty += q;
        byCard[name].fee += f;
        dayTotal += f;
      }
      for (const k of Object.keys(byCard)) {
        byCard[k].fee = Math.round(byCard[k].fee * 100) / 100;
      }
      rows.push({
        dateKey,
        withdrawDateDisplay: formatDate(dateKey),
        byCard,
        dayTotalFee: Math.round(dayTotal * 100) / 100,
        records: [...records].sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0)),
      });
    }
    rows.sort((a, b) => b.dateKey.localeCompare(a.dateKey));
    return rows;
  }, [withdrawRecords]);

  const expandedDayRecords = useMemo(() => {
    if (!expandedDateKey) return [];
    const row = daySummaryRows.find((d) => d.dateKey === expandedDateKey);
    return row?.records || [];
  }, [expandedDateKey, daySummaryRows]);

  const handleChange = (e) => {
    if(e.target){
      const newCardName = e.target.value;
      const selected = travelCards?.find((c) => c.cardName === newCardName);

      setFormData((prev) => ({
        ...prev,
        cardName: newCardName,
        cashWithdrawFee: selected ? (selected.feeThbPer100k ?? 0) : '',
        cashWithdrawFeeJpy: selected ? (selected.feeJpyPer100k ?? 0) : '',
      }));
    }
  };

  const handleDateChange = (name, selectedDate) => {
    setFormData(prevOrder => ({
        ...prevOrder,
        [name]: selectedDate
    }));
	};

  const handleInputChange = (name, value) => {
    if (numberFields.has(name)) {
			value = validateNumberInput(value, formData[name]);
		}
    setFormData(formData => ({
      ...formData,
      [name]:value
    }))
  }

  const calcTotalFeeFor = (cardName, qty, rate) => {
    const card = travelCards?.find((c) => c.cardName === cardName);
    const feeThb = Number(card?.feeThbPer100k ?? 0);
    const feeJpy = Number(card?.feeJpyPer100k ?? 0);
    const qtyN = Number(qty);
    const r = Number(rate ?? 0);
    if (!Number.isFinite(qtyN) || qtyN <= 0) return null;
    if (!Number.isFinite(feeThb) || !Number.isFinite(feeJpy)) return null;
    if (feeJpy !== 0 && !Number.isFinite(r)) return null;
    const perUnitThb = feeThb + feeJpy * (feeJpy !== 0 ? r : 0);
    const total = qtyN * perUnitThb;
    return Math.round(total * 100) / 100;
  };

  const addBulkItem = () => {
    if (!bulkCardName) {
      toast.error('Please select a card');
      return;
    }
    const qtyN = Number(bulkQty);
    if (!Number.isFinite(qtyN) || qtyN <= 0) {
      toast.error('Please input qty');
      return;
    }
    const rate = rateInfo?.standardRate;
    const totalFee = calcTotalFeeFor(bulkCardName, qtyN, rate);
    if (totalFee == null) {
      toast.error('Could not compute total fee (missing rate?)');
      return;
    }
    setBulkItems((prev) => ([
      ...prev,
      { cardName: bulkCardName, qty: qtyN, totalFee }
    ]));
    setBulkCardName('');
    setBulkQty('');
  };

  const removeBulkItem = (idx) => {
    setBulkItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const submitBulk = async () => {
    if (bulkItems.length === 0) {
      toast.error('No items to submit');
      return;
    }
    const withdrawDate = formatLocalYMD(formData.withdrawDate);
    const ok = await saveWithdrawRecordsBulk(
      withdrawDate,
      bulkItems.map(({ cardName, qty }) => ({ cardName, qty }))
    );
    if (ok) {
      toast.success('Saved bulk withdraw records');
      setBulkItems([]);
      setFormData((prev) => ({ ...getInitialFormData(), withdrawDate: prev.withdrawDate }));
      setEditingId(null);
    } else {
      toast.error('Bulk save failed');
    }
  };

  useEffect(() => {
    const dateStr = formatLocalYMD(formData.withdrawDate);
    if (!dateStr) return;
    let cancelled = false;
    setRateError(null);
    setRateInfo(null);
    (async () => {
      try {
        const res = await WithdrawRecordService.getCurrencyRateByDate(dateStr);
        if (!cancelled) {
          setRateInfo(res.data);
        }
      } catch (e) {
        if (!cancelled) {
          setRateInfo(null);
          setRateError(e.response?.data?.message || 'Could not load exchange rate for this date');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [formData.withdrawDate]);

  useEffect(() => {
    const qtyN = parseFloat(formData.qty);
    const feeThbN = parseFloat(formData.cashWithdrawFee);
    const feeJpyN = parseFloat(formData.cashWithdrawFeeJpy);
    const rate = rateInfo?.standardRate;
    const needsFx = Number.isFinite(feeJpyN) && feeJpyN !== 0;

    if (!Number.isFinite(qtyN) || !Number.isFinite(feeThbN) || !Number.isFinite(feeJpyN)) {
      setFormData((prev) => ({ ...prev, totalFee: '' }));
      return;
    }
    if (needsFx && !Number.isFinite(rate)) {
      setFormData((prev) => ({ ...prev, totalFee: '' }));
      return;
    }
    const r = Number.isFinite(rate) ? rate : 0;
    const perUnitThb = feeThbN + feeJpyN * r;
    const total = qtyN * perUnitThb;
    setFormData((prev) => ({
      ...prev,
      totalFee: Number.isFinite(total) ? Math.round(total * 100) / 100 : '',
    }));
  }, [formData.qty, formData.cashWithdrawFee, formData.cashWithdrawFeeJpy, rateInfo]);
  
  const handleSaveButton = async (e) => {
    e.preventDefault()
    const response = await saveWithdrawRecord({ ...formData, id: editingId })
    if(response){
      toast.success(editingId ? "Update Withdraw Record Successfully!!" : "Add Withdraw Record Successfully!!")
      setFormData((prev) => ({ ...getInitialFormData(), withdrawDate: prev.withdrawDate }))
      setEditingId(null)
      setRateInfo(null)
      setRateError(null)
    }
  }

  const handleRowClick = (row) => {
    if (!row) return;
    const parsedDate = row.withdrawDate ? new Date(row.withdrawDate) : new Date();
    setEditingId(row.id);
    setFormData({
      ...getInitialFormData(),
      withdrawDate: parsedDate,
      cardName: row.cardName || '',
      qty: row.qty ?? '',
      cashWithdrawFee: row.withdrawFee ?? '',
      cashWithdrawFeeJpy: row.withdrawFeeJpy ?? '',
      totalFee: row.totalFee ?? '',
    });
  };

  const handleDelete = async (row) => {
    if (!row?.id) return;
    if (!window.confirm('Delete this withdraw record?')) return;
    const wasEditing = editingId === row.id;
    try {
      await WithdrawRecordService.deleteWithdrawRecord(row.id);
      toast.success('Deleted withdraw record');
      await getWithdrawRecords();
      if (wasEditing) {
        setEditingId(null);
        setFormData((prev) => ({ ...getInitialFormData(), withdrawDate: prev.withdrawDate }));
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  };

  const toggleDayDetails = useCallback((dateKey) => {
    setExpandedDateKey((prev) => (prev === dateKey ? null : dateKey));
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Date',
        field: 'dateKey',
        flex: 0.9,
        minWidth: 120,
        sortable: true,
        sort: 'desc',
        valueFormatter: (p) => (p.data ? p.data.withdrawDateDisplay : ''),
      },
      {
        headerName: 'By card (× = withdraw units / qty)',
        field: 'byCard',
        flex: 2.2,
        minWidth: 280,
        cellRenderer: CardBreakdownCell,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: 'Day total (THB)',
        field: 'dayTotalFee',
        flex: 0.8,
        minWidth: 130,
        valueFormatter: (p) => (p.value == null ? '' : `฿${formatAmount(p.value)}`),
      },
      {
        headerName: '',
        field: 'actions',
        flex: 0.5,
        minWidth: 100,
        cellRenderer: (params) => (
          <button
            type="button"
            className="btn btn-save text-sm px-2 py-1"
            onClick={(e) => {
              e.stopPropagation();
              toggleDayDetails(params.data.dateKey);
            }}
          >
            {expandedDateKey === params.data.dateKey ? 'Hide' : 'Details'}
          </button>
        ),
      },
    ],
    [toggleDayDetails, expandedDateKey]
  );

  useEffect(() => {
		refreshTravelCards()
    getWithdrawRecords()
	},[])

  useEffect(() => {
    if (!expandedDateKey) return;
    const stillExists = daySummaryRows.some((r) => r.dateKey === expandedDateKey);
    if (!stillExists) setExpandedDateKey(null);
  }, [expandedDateKey, daySummaryRows]);

  return (
    <div className="ag-theme-alpine" style={{ width: '100%' }}>
      <h2>
        Withdraw Record
      </h2>
      
      <div className='grid grid-cols-3'>
        <div className='col-span-2'>
          <p className="mb-3 text-sm leading-relaxed text-slate-600">
            <span className="font-semibold text-slate-800">Daily summary</span>
            {' — '}
            one row per calendar day. Each chip is one travel card:{' '}
            <strong>×</strong> is total withdraw units (qty) that day, and{' '}
            <strong>฿</strong> is fee for that card on that day. Use <strong>Details</strong> to edit or delete individual lines.
          </p>
          <AgGridReact
            rowData={daySummaryRows}
            columnDefs={columnDefs}
            getRowId={(p) => p.data.dateKey}
            domLayout="autoHeight"
            defaultColDef={{ resizable: true }}
            suppressRowClickSelection
          />
          {expandedDateKey ? (
            <div className="mt-5 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 bg-gradient-to-r from-slate-100 to-slate-50 px-4 py-2.5">
                <span className="font-semibold text-slate-800">
                  Detail lines — {formatDate(expandedDateKey)}
                </span>
                <span className="ml-2 text-sm font-normal text-slate-500">
                  (click a row to load into the form for edit)
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-600">
                      <th className="px-4 py-2.5 font-semibold">ID</th>
                      <th className="px-4 py-2.5 font-semibold">Card</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Qty</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Fee (THB)</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expandedDayRecords.map((rec) => (
                      <tr
                        key={rec.id}
                        className="cursor-pointer border-b border-slate-100 hover:bg-blue-50/60"
                        onClick={() => handleRowClick(rec)}
                      >
                        <td className="px-4 py-2 text-slate-700">{rec.id}</td>
                        <td className="px-4 py-2 font-medium text-slate-900">{rec.cardName}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{rec.qty}</td>
                        <td className="px-4 py-2 text-right tabular-nums">฿{formatAmount(rec.totalFee)}</td>
                        <td className="px-4 py-2 text-right">
                          <button
                            type="button"
                            className="btn btn-delete text-xs px-2 py-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(rec);
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
        <div className='col-span-1'>
          <form className='mx-5 border-gray-300 border rounded-lg'>
            <div className='bg-gray-200 px-4 py-2 rounded-t-lg border-t'>
              <h3 className='text-3xl '>Input New Data</h3>
            </div>
            <div className='p-5 mx-1'>
            <InputLabel 
                id="cardName-label" 
                style={{
                  fontSize:'1.1em',
                  color:'black',
                  paddingLeft:'2px'
                }}
              >
                Withdraw Date
              </InputLabel>
              <DatePicker 
								selected={formData?.withdrawDate}
								onChange={(date) => handleDateChange("withdrawDate", date)}
								dateFormat={"yyyy-MM-dd"}
								className='datepicker2 w-full mb-4'
							/>

              <div className="mb-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
                <div className="font-semibold mb-2">Bulk add (same date)</div>

                <InputLabel
                  id="bulk-card-label"
                  style={{ fontSize:'1.1em', color:'black', paddingLeft:'2px' }}
                >
                  Travel Card
                </InputLabel>
                <FormControl fullWidth size="small">
                  <Select
                    labelId="bulk-card-label"
                    id="bulkCardName"
                    value={bulkCardName}
                    onChange={(e) => setBulkCardName(e.target.value)}
                  >
                    {travelCards && travelCards.map((card) => (
                      card?.isActive !== false && (
                        <MenuItem key={`bulk-${card.id}`} value={card.cardName}>{card.cardName}</MenuItem>
                      )
                    ))}
                  </Select>
                </FormControl>

                <div className='mt-3'></div>
                <Input title={'Qty'} name={'bulkQty'} value={bulkQty} onChange={value => setBulkQty(validateNumberInput(value, bulkQty))} />

                <div className="flex gap-2 mt-2">
                  <button type="button" className="w-32 btn btn-save text-lg" onClick={addBulkItem}>Add</button>
                  <button type="button" className="w-32 btn btn-save text-lg" onClick={submitBulk} disabled={bulkItems.length === 0}>Submit All</button>
                </div>

                {bulkItems.length > 0 ? (
                  <div className="mt-3 text-sm">
                    {bulkItems.map((it, idx) => (
                      <div key={`${it.cardName}-${idx}`} className="flex items-center justify-between border-b border-gray-200 py-1">
                        <div>
                          <span className="font-semibold">{it.cardName}</span> — qty {it.qty} — fee {it.totalFee}
                        </div>
                        <button type="button" className="btn btn-delete text-sm" onClick={() => removeBulkItem(idx)}>Remove</button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              <InputLabel 
                id="cardName-label" 
                style={{
                  fontSize:'1.1em',
                  color:'black',
                  paddingLeft:'2px'
                }}
              >
                Travel Card
              </InputLabel>
              <FormControl fullWidth size="small">
                <Select
                  labelId="cardName-label"
                  id="cardName"
                  value={formData?.cardName}
                  onChange={handleChange}
                >
                  {travelCards && travelCards.map((card) => (
                    card?.isActive !== false && (
                      <MenuItem key={card.id} value={card.cardName}>{card.cardName}</MenuItem>
                    )
                  ))}
                </Select>
              </FormControl>
              <div className='mt-3'></div>
              <Input title={'Qty'} name={'qty'} value={formData?.qty} onChange={value => handleInputChange('qty',value)} /* customClass={'mb-4'} *//>
              <Input title={'Withdraw Fee'} name={'cashWithdrawFee'} value={formData?.cashWithdrawFee} onChange={value => handleInputChange('cashWithdrawFee', value)} disable={true}/>
              <Input title={'Withdraw Fee (JPY)'} name={'cashWithdrawFeeJpy'} value={formData?.cashWithdrawFeeJpy} disable={true}/>
              {rateInfo && (
                <div className="text-sm text-gray-800 mb-3 mt-1 space-y-0.5 border border-gray-200 rounded-md p-2 bg-gray-50">
                  <div>Cost rate (THB/JPY): {Number(rateInfo.standardRate).toFixed(4)}</div>
                  <div className="text-gray-600">As of: {rateInfo.lastUpdated ? formatDate(rateInfo.lastUpdated) : '—'}</div>
                </div>
              )}
              {rateError && (
                <p className="text-sm text-red-600 mb-2">{rateError}</p>
              )}
              <Input title={'Total Fee (THB)'} name={'totalFee'} value={formData?.totalFee} onChange={value => handleInputChange('totalFee',value)} disable={true} /* customClass={'mb-4'} *//>
              <div className="flex gap-2">
                <button className='w-32 btn btn-save text-lg' onClick={handleSaveButton}>
                  {editingId ? 'Update' : 'Save'}
                </button>
                {editingId ? (
                  <button
                    type="button"
                    className='w-32 btn btn-delete text-lg'
                    onClick={() => {
                      setEditingId(null);
                      setFormData((prev) => ({ ...getInitialFormData(), withdrawDate: prev.withdrawDate }));
                    }}
                  >
                    Cancel
                  </button>
                ) : null}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default WithdrawRecord